import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Button from '../components/base/Button';
import FormField from '../components/FormField';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Logo } from '../components/App';
import Icons from '../components/Icons';
import { passwordSchema } from '../shared/utils/formikValidators';
import { Alert, Snackbar } from '@mui/material';
import AuthService from '../services/AuthService';

const loginIdSchema = Yup.string()
  .trim()
  .required('Enter your email or mobile number')
  .test(
    'mobile-or-email',
    'Enter a valid email address or 10 digit mobile number',
    (value) => {
      if (!value) return false;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const mobileRegex = /^[0-9]{10}$/;
      return emailRegex.test(value) || mobileRegex.test(value);
    },
  );

function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState('request');
  const [loginId, setLoginId] = useState('');
  const [verificationId, setVerificationId] = useState('');
  const [visible, setVisible] = useState(false);
  const [visible2, setVisible2] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const $loginRef = useRef();

  const requestFormik = useFormik({
    initialValues: { loginId: '' },
    validationSchema: Yup.object({ loginId: loginIdSchema }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const id = await AuthService.requestPasswordReset(values.loginId);
        setLoginId(values.loginId.trim());
        setVerificationId(id);
        setPhase('reset');
        setSnackbar({
          open: true,
          message: 'Check your email or SMS for the verification code.',
          severity: 'success',
        });
      } catch (e) {
        setSnackbar({
          open: true,
          message: e?.message || 'Could not send reset code',
          severity: 'error',
        });
      } finally {
        setSubmitting(false);
      }
    },
  });

  const resetFormik = useFormik({
    initialValues: {
      code: '',
      password: '',
      confirmPassword: '',
    },
    validationSchema: Yup.object({
      code: Yup.string()
        .trim()
        .required('Enter the verification code')
        .matches(/^[0-9]{4}$/, 'Code must be 4 digits'),
      password: passwordSchema,
      confirmPassword: Yup.string()
        .trim()
        .required('Confirm your password')
        .oneOf([Yup.ref('password')], 'Passwords must match'),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        await AuthService.resetPassword({
          verificationId,
          verificationCode: values.code.trim(),
          loginId,
          password: values.password.trim(),
        });
        setSnackbar({
          open: true,
          message: 'Password updated. You can sign in now.',
          severity: 'success',
        });
        setTimeout(() => navigate('/signin', { replace: true }), 1200);
      } catch (e) {
        setSnackbar({
          open: true,
          message: e?.message || 'Reset failed',
          severity: 'error',
        });
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    document.title = 'Reset password · WappGrow';
  }, []);

  useEffect(() => {
    $loginRef.current?.focus?.();
  }, []);

  const cardStyle = {
    width: '100%',
    maxWidth: 400,
    background: '#fff',
    borderRadius: 16,
    boxShadow: '0 2px 16px rgba(215, 225, 255, 0.94)',
    padding: '32px 24px',
    margin: '32px 0',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div style={cardStyle}>
        <Logo />
        <div
          style={{
            marginTop: 16,
            marginBottom: 8,
            fontWeight: 700,
            fontSize: 22,
            color: '#222',
          }}
        >
          {phase === 'request' ? 'Forgot password' : 'Set new password'}
        </div>
        <div
          style={{
            color: '#666',
            fontSize: 15,
            marginBottom: 24,
            textAlign: 'center',
          }}
        >
          {phase === 'request'
            ? 'Enter the email or mobile number you use to sign in. We will send a 4-digit code.'
            : `Code sent for ${loginId}. Enter the code and choose a new password.`}
        </div>

        {phase === 'request' ? (
          <form onSubmit={requestFormik.handleSubmit} style={{ width: '100%' }}>
            <div style={{ marginBottom: 18 }}>
              <FormField
                ref={$loginRef}
                type="auth-text"
                name="loginId"
                placeholder="Email or 10-digit mobile"
                value={requestFormik.values.loginId}
                onChange={requestFormik.handleChange}
                onBlur={requestFormik.handleBlur}
                error={
                  requestFormik.touched.loginId && requestFormik.errors.loginId
                }
                style={{
                  fontSize: 15,
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid #e0e0e0',
                  background: '#f8f9fa',
                }}
              />
            </div>
            <Button
              type="submit"
              appearance="auth-btn"
              disabled={requestFormik.isSubmitting}
              style={{
                width: '100%',
                padding: '12px',
                fontSize: 16,
                borderRadius: 8,
                background: '#1976d2',
                color: '#fff',
                fontWeight: 600,
                border: 'none',
                marginBottom: 16,
                cursor: 'pointer',
              }}
            >
              {requestFormik.isSubmitting ? 'Sending…' : 'Send code'}
            </Button>
          </form>
        ) : (
          <form onSubmit={resetFormik.handleSubmit} style={{ width: '100%' }}>
            <div style={{ marginBottom: 18 }}>
              <FormField
                type="auth-text"
                name="code"
                placeholder="4-digit code"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={resetFormik.values.code}
                onChange={resetFormik.handleChange}
                onBlur={resetFormik.handleBlur}
                error={resetFormik.touched.code && resetFormik.errors.code}
                style={{
                  fontSize: 15,
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid #e0e0e0',
                  background: '#f8f9fa',
                }}
              />
            </div>
            <div style={{ marginBottom: 18 }}>
              <FormField
                name="password"
                placeholder="New password"
                value={resetFormik.values.password}
                onChange={resetFormik.handleChange}
                onBlur={resetFormik.handleBlur}
                error={
                  resetFormik.touched.password && resetFormik.errors.password
                }
                type={visible ? 'text' : 'password'}
                iconSuffix={
                  resetFormik.values.password && (
                    <div
                      style={{ cursor: 'pointer' }}
                      onClick={() => setVisible(!visible)}
                    >
                      {visible ? Icons.Visible : Icons.VisibleOff}
                    </div>
                  )
                }
                style={{
                  fontSize: 15,
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid #e0e0e0',
                  background: '#f8f9fa',
                }}
              />
            </div>
            <div style={{ marginBottom: 18 }}>
              <FormField
                name="confirmPassword"
                placeholder="Confirm new password"
                value={resetFormik.values.confirmPassword}
                onChange={resetFormik.handleChange}
                onBlur={resetFormik.handleBlur}
                error={
                  resetFormik.touched.confirmPassword
                  && resetFormik.errors.confirmPassword
                }
                type={visible2 ? 'text' : 'password'}
                iconSuffix={
                  resetFormik.values.confirmPassword && (
                    <div
                      style={{ cursor: 'pointer' }}
                      onClick={() => setVisible2(!visible2)}
                    >
                      {visible2 ? Icons.Visible : Icons.VisibleOff}
                    </div>
                  )
                }
                style={{
                  fontSize: 15,
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid #e0e0e0',
                  background: '#f8f9fa',
                }}
              />
            </div>
            <Button
              type="submit"
              appearance="auth-btn"
              disabled={resetFormik.isSubmitting}
              style={{
                width: '100%',
                padding: '12px',
                fontSize: 16,
                borderRadius: 8,
                background: '#1976d2',
                color: '#fff',
                fontWeight: 600,
                border: 'none',
                marginBottom: 12,
                cursor: 'pointer',
              }}
            >
              {resetFormik.isSubmitting ? 'Saving…' : 'Update password'}
            </Button>
            <Button
              type="button"
              appearance="auth-btn"
              onClick={() => {
                setPhase('request');
                resetFormik.resetForm();
              }}
              style={{
                width: '100%',
                padding: '10px',
                fontSize: 14,
                borderRadius: 8,
                background: 'transparent',
                color: '#1976d2',
                fontWeight: 600,
                border: '1px solid #1976d2',
                cursor: 'pointer',
              }}
            >
              Use a different email or number
            </Button>
          </form>
        )}

        <div style={{ marginTop: 20, fontSize: 15, textAlign: 'center' }}>
          <Link to="/signin" style={{ color: '#1976d2' }}>
            Back to sign in
          </Link>
        </div>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          <Alert
            onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    </div>
  );
}

export { ForgotPasswordPage };
